import React, { useCallback, useEffect, useState } from 'react';
import { invoke } from '@tauri-apps/api/core';
import { Row } from '../settingsPrimitives';
import { isTauri } from '../../utils/tauri';

/**
 * Settings → MCP → **Terminal**: which of the user's saved SSH connections a
 * connected client may use, and what it has done with them.
 *
 * The two questions a user must be able to answer about this surface are
 * *which servers can it touch* and *what has it done*, so the allowlist and the
 * audit log are adjacent rather than in separate sections.
 *
 * **This component calls `vault_list`, and the MCP server never does.** That
 * asymmetry is the whole design (PLAN §D). SSH connections are Vault items, and
 * D4's rule is flat: the Vault is never exposed over MCP, *including item
 * names*. This is Apiboo's own window reading the user's own Vault so they can
 * tick boxes — exactly what `ftpStore.loadConnections` already does. What the
 * server reads is the resulting allowlist FILE, which holds only the ids and
 * names the user chose to put there.
 *
 * **There is no "all", no select-all and no wildcard**, deliberately. The empty
 * list is the default and a connection nobody ticked does not exist as far as
 * the server is concerned.
 *
 * Vault locked → the list cannot be changed, and the connections already
 * granted are shown by their stored name so the user can still see what is
 * allowed. A connection that no longer resolves is shown greyed with a Remove
 * button rather than pruned in the background: a Vault that is merely locked
 * must never be read as "deleted", and silently dropping a grant the user set
 * is a change they did not make.
 */

/** Newest first, and enough of them that a bad afternoon is still visible. */
const AUDIT_LIMIT = 50;

function formatWhen(ms) {
  if (!Number.isFinite(ms) || ms <= 0) return '';
  try {
    return new Date(ms).toLocaleString();
  } catch {
    return '';
  }
}

export default function McpTerminalGrants({ t }) {
  const [allowed, setAllowed] = useState([]);
  const [vaultRows, setVaultRows] = useState(null);
  const [vaultLocked, setVaultLocked] = useState(false);
  const [audit, setAudit] = useState({ entries: [], unreadable: 0, total: 0 });
  // A silent catch is indistinguishable from a dead control — the 09.09.2026
  // lesson, applied to the one page whose whole job is to tell the truth about
  // what happened.
  const [error, setError] = useState(null);

  const refresh = useCallback(async () => {
    if (!isTauri()) return;
    try {
      const [list, page] = await Promise.all([
        invoke('mcp_terminal_allowlist_get'),
        invoke('mcp_terminal_audit', { limit: AUDIT_LIMIT }),
      ]);
      setAllowed(Array.isArray(list) ? list : []);
      setAudit(page || { entries: [], unreadable: 0, total: 0 });
      setError(null);
    } catch (err) {
      setError(String(err?.message || err));
    }
    // The Vault read is separate and is allowed to fail on its own: a locked
    // Vault must not blank the allowlist or the log, both of which are
    // readable without it.
    try {
      const items = await invoke('vault_list');
      setVaultRows(
        (Array.isArray(items) ? items : [])
          .filter((i) => i?.type === 'sftp' && i?.index?.protocol === 'sftp')
          .map((i) => ({
            id: String(i.id),
            name: String(i.name || ''),
            host: String(i.index?.host || ''),
            username: String(i.index?.username || ''),
            port: Number(i.index?.port) || 22,
          }))
      );
      setVaultLocked(false);
    } catch {
      setVaultRows(null);
      setVaultLocked(true);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const write = useCallback(
    async (connections) => {
      try {
        const next = await invoke('mcp_terminal_allowlist_set', { connections });
        setAllowed(Array.isArray(next) ? next : []);
        setError(null);
      } catch (err) {
        setError(String(err?.message || err));
      }
    },
    []
  );

  const toggle = useCallback(
    (row, on) => {
      const rest = allowed.filter((c) => c.id !== row.id);
      if (!on) return write(rest);
      return write([
        ...rest,
        { id: row.id, name: row.name, grantedAt: new Date().toISOString() },
      ]);
    },
    [allowed, write]
  );

  const remove = useCallback((id) => write(allowed.filter((c) => c.id !== id)), [allowed, write]);

  const allowedIds = new Set(allowed.map((c) => c.id));
  // Rows the user granted that the Vault no longer resolves. Only meaningful
  // when the Vault is actually readable — a locked Vault resolves nothing, and
  // calling all of them "gone" would be a lie.
  const orphaned =
    vaultRows === null ? [] : allowed.filter((c) => !vaultRows.some((r) => r.id === c.id));

  return (
    <>
      <div className="settings-row-desc mcp-note">{t('mcp.terminalAllowlistDesc')}</div>

      {error && (
        <div className="mcp-backend-error" role="alert">
          {t('mcp.backendUnavailable')}
          <div className="mcp-backend-error-detail">{error}</div>
        </div>
      )}

      {vaultLocked ? (
        <>
          <div className="settings-row-desc">{t('mcp.terminalVaultLocked')}</div>
          {allowed.length === 0 ? (
            <div className="settings-row-desc">{t('mcp.terminalNoneAllowed')}</div>
          ) : (
            <ul className="mcp-activity">
              {allowed.map((c) => (
                <li key={c.id} className="mcp-activity-row">
                  <span className="mcp-activity-tool">{c.name || c.id}</span>
                </li>
              ))}
            </ul>
          )}
        </>
      ) : (
        <>
          {(vaultRows || []).length === 0 && (
            <div className="settings-row-desc">{t('mcp.terminalNoConnections')}</div>
          )}
          {(vaultRows || []).map((row) => (
            <Row
              key={row.id}
              label={row.name || row.id}
              desc={`${row.username ? `${row.username}@` : ''}${row.host}:${row.port}`}
            >
              <input
                type="checkbox"
                className="checkbox-brand"
                aria-label={row.name || row.id}
                checked={allowedIds.has(row.id)}
                onChange={(e) => toggle(row, e.target.checked)}
              />
            </Row>
          ))}
          {orphaned.map((c) => (
            <Row key={c.id} label={c.name || c.id} desc={t('mcp.terminalGone')}>
              <button type="button" className="settings-action-btn" onClick={() => remove(c.id)}>
                {t('mcp.terminalRemove')}
              </button>
            </Row>
          ))}
        </>
      )}

      {/* **O5, and the honest limit of this surface, in the user's own words.**
          One plain sentence at ordinary body-copy weight — no banner, no ⚠, no
          coloured strip (owner amendment to O5). It sits here rather than in
          the capability matrix because it is about the servers listed directly
          above it. */}
      <div className="settings-row-desc mcp-note">{t('mcp.terminalOutputNote')}</div>

      <div className="settings-row-desc mcp-note mcp-terminal-audit-head">
        {t('mcp.terminalAuditDesc')}
      </div>
      {audit.entries.length === 0 ? (
        <div className="settings-row-desc">{t('mcp.terminalAuditEmpty')}</div>
      ) : (
        <ul className="mcp-activity mcp-terminal-audit">
          {audit.entries.map((e, i) => (
            <li key={`${e.approvalId || 'x'}-${e.at}-${i}`} className="mcp-activity-row">
              <span className="mcp-activity-verdict">{formatWhen(e.at)}</span>
              <span className="mcp-activity-tool">{e.connName || e.connId || '—'}</span>
              <span className="mcp-activity-verdict">
                {t(`mcp.terminalDecision_${e.decision}`, { defaultValue: e.decision })}
                {/* Where it ran (13.09.2026): a watched tab, the hidden channel,
                    or an SFTP write. A fixed label from Rust's closed set. */}
                {e.runMode ? ` · ${t(`mcp.terminalRunMode_${e.runMode}`, { defaultValue: e.runMode })}` : ''}
                {e.outcome ? ` · ${e.outcome}` : ''}
                {Number.isFinite(e.exitCode) ? ` · exit ${e.exitCode}` : ''}
              </span>
              {/* The command, in FULL. A truncated audit record of a command
                  whose payload is in its tail is worse than none — the same
                  defect as an approval card that shows a prefix and runs the
                  whole thing. It wraps and scrolls; it is never sliced. */}
              <pre className="mcp-terminal-audit-cmd">{e.command}</pre>
            </li>
          ))}
        </ul>
      )}
      {audit.unreadable > 0 && (
        <div className="settings-row-desc">
          {t('mcp.terminalAuditUnreadable', { count: audit.unreadable })}
        </div>
      )}
    </>
  );
}
